import React, { useContext, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import {
  getCanCreateAndEditTestSuites,
  isGremlinOfficialTestSuite
} from 'components/test-suites/test-suites-utils'
import useAuth from 'hooks/use-auth'
import { useGetScenariosForTeams } from 'hooks/use-get-scenarios-for-teams'
import { useGetTestSuite } from 'queries/test-suites/use-get-test-suite'
import { useGetTestSuitesDetectedRisks } from 'queries/test-suites/use-get-test-suites-detected-risks'

import type { ScenarioForTestSuite } from 'components/test-suites/forms/test-suite-form-utils'
import type { TestSuite } from 'types/api/test-suites/test-suites'

export enum TestSuiteFormMode {
  Clone = 'Clone',
  Edit = 'Edit',
  New = 'New'
}

export interface TestSuiteFormContextValues {
  canCreateAndEditTestSuites: boolean
  isErrorScenarios: boolean
  isLoadingScenarios: boolean
  isOfficialTestSuite: boolean
  scenariosAggregated: ScenarioForTestSuite[]
  scenariosLookup: Map<string, ScenarioForTestSuite>
  queryResultTestSuite: ReturnType<typeof useGetTestSuite>
  queryResultTestSuitesDetectedRisks: ReturnType<
    typeof useGetTestSuitesDetectedRisks
  >
  testSuiteClone: TestSuite | undefined
  testSuiteToEdit: TestSuite | undefined
  testSuiteFormMode: TestSuiteFormMode
}

export const TestSuiteFormContext = React.createContext<
  TestSuiteFormContextValues | undefined
>(undefined)

export function useTestSuiteFormContext() {
  const context = useContext(TestSuiteFormContext)

  if (!context) {
    throw new Error(
      'useTestSuiteFormContext must be used within a TestSuiteFormContextProvider'
    )
  }

  return context
}

export const TestSuiteFormContextProvider: React.FC<{
  isEdit?: boolean
}> = ({ children, isEdit = false }) => {
  const { hasEndpointAccess } = useAuth()
  const canCreateAndEditTestSuites =
    getCanCreateAndEditTestSuites(hasEndpointAccess)

  const location = useLocation()

  // Create mode can be 'new' or 'clone'
  const testSuite: TestSuite | undefined = location?.state?.testSuite
  const isClone = isEdit ? false : Boolean(testSuite)

  // isEdit will have a testSuiteId in the route
  const { testSuiteId } = useParams()

  const queryResultTestSuitesDetectedRisks = useGetTestSuitesDetectedRisks()

  const queryResultTestSuite = useGetTestSuite(testSuiteId, {
    enabled: Boolean(isEdit && testSuiteId),
    placeholderData: location.state?.testSuite as TestSuite
  })

  const isOfficialTestSuite =
    isEdit && queryResultTestSuite.data
      ? isGremlinOfficialTestSuite(queryResultTestSuite.data)
      : false

  const {
    publishedAndDraftScenarios,
    isFetchingPublishedAndDraftScenarios,
    hasErrorPublishedAndDraftScenarios,
    sharedScenarios,
    isFetchingSharedScenarios,
    hasErrorSharedScenarios,
    recommendedScenarios,
    isFetchingRecommendedScenarios,
    hasErrorRecommendedScenarios
  } = useGetScenariosForTeams()

  const isLoadingScenarios =
    isFetchingPublishedAndDraftScenarios ||
    isFetchingSharedScenarios ||
    isFetchingRecommendedScenarios

  const isErrorScenarios =
    hasErrorPublishedAndDraftScenarios ||
    hasErrorSharedScenarios ||
    hasErrorRecommendedScenarios

  const scenariosLookup = useMemo(() => {
    if (isLoadingScenarios || isErrorScenarios) {
      return new Map()
    }

    const aggregatedScenarios = [
      ...publishedAndDraftScenarios,
      ...(sharedScenarios ?? []),
      ...(recommendedScenarios ?? [])
    ]

    return new Map(
      aggregatedScenarios.map((scenario) => [scenario.guid, scenario])
    )
  }, [
    isErrorScenarios,
    isLoadingScenarios,
    publishedAndDraftScenarios,
    recommendedScenarios,
    sharedScenarios
  ])

  const scenariosAggregated = useMemo(
    () => Array.from(scenariosLookup.values()),
    [scenariosLookup]
  )

  const testSuiteFormMode: TestSuiteFormMode = useMemo(() => {
    if (isClone) return TestSuiteFormMode.Clone
    return isEdit ? TestSuiteFormMode.Edit : TestSuiteFormMode.New
  }, [isClone, isEdit])

  const contextValue = useMemo(
    () => ({
      canCreateAndEditTestSuites,
      isErrorScenarios,
      isLoadingScenarios,
      isOfficialTestSuite,
      scenariosAggregated,
      scenariosLookup,
      queryResultTestSuite,
      queryResultTestSuitesDetectedRisks,
      testSuiteClone:
        testSuiteFormMode === TestSuiteFormMode.Clone && testSuite
          ? testSuite
          : undefined,
      testSuiteToEdit:
        testSuiteFormMode === TestSuiteFormMode.Edit &&
        queryResultTestSuite.data
          ? queryResultTestSuite.data
          : undefined,
      testSuiteFormMode
    }),
    [
      canCreateAndEditTestSuites,
      isErrorScenarios,
      isLoadingScenarios,
      isOfficialTestSuite,
      scenariosAggregated,
      scenariosLookup,
      queryResultTestSuite,
      queryResultTestSuitesDetectedRisks,
      testSuite,
      testSuiteFormMode
    ]
  )

  return (
    <TestSuiteFormContext.Provider value={contextValue}>
      {children}
    </TestSuiteFormContext.Provider>
  )
}
